if (!customElements.get('pb40-terra-thumbnails')) {
  customElements.define('pb40-terra-thumbnails', class extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      await Promise.all(['media-gallery', 'slider-component', 'product-modal'].map(name => customElements.whenDefined(name)));
      if (!this.isConnected) { this.ready = false; return; }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.gallery = this.closest('media-gallery');
      this.viewer = this.gallery.querySelector('[id^="GalleryViewer"]');
      this.slider = this.viewer.querySelector('[id^="Slider-Gallery"]');
      this.strip = this.querySelector('.pb40-terra-thumbnails');
      this.status = this.querySelector('[data-photo-status]');
      this.modal = document.getElementById(`ProductModal-${this.dataset.sectionId}`);
      this.buttons = [...this.strip.querySelectorAll('[data-photo-id]')].sort((a, b) => +a.dataset.photoOrder - +b.dataset.photoOrder);
      this.buttons.forEach(button => this.strip.append(button));
      this.orderSlides(this.slider, false);
      this.slides = [...this.slider.children].filter(slide => slide.dataset.productMediaId);
      this.viewer._initHard?.();
      this.viewer._markSlidesDirty?.();
      this.viewer.resetPages?.();
      this.strip.addEventListener('click', event => {
        const button = event.target.closest('[data-photo-id]');
        if (button) this.select(button.dataset.photoId);
      }, { signal });
      this.gallery.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !event.target.closest('.product__media-toggle, .pb40-terra-thumbnail, .pb40-terra-gallery-controls')) return;
        const visible = this.buttons.filter(button => !button.hidden);
        const index = visible.findIndex(button => button.dataset.photoId === this.activeId);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1 : Math.min(visible.length - 1, Math.max(0, index + (event.key === 'ArrowRight' ? 1 : -1)));
        event.preventDefault();
        if (visible[next]) {
          this.select(visible[next].dataset.photoId);
          visible[next].focus({ preventScroll: true });
        }
      }, { signal });
      this.mainObserver = new MutationObserver(() => this.queueSync());
      this.mainObserver.observe(this.slider, { subtree: true, attributes: true, attributeFilter: ['class'] });
      this.slider.addEventListener('scrollend', () => this.sync(), { signal });
      this.resizeObserver = new ResizeObserver(() => this.queueSync());
      this.resizeObserver.observe(this.slider);
      if (this.modal) this.connectModal(signal);
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.mainObserver?.disconnect();
      this.modalObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.syncFrame);
      cancelAnimationFrame(this.modalFrame);
      this.ready = false;
    }

    orderSlides(slider, modal) {
      if (!slider) return;
      const items = [...slider.children];
      this.buttons.forEach(button => {
        const item = items.find(slide => modal ? slide.querySelector(`[data-media-id="${button.dataset.photoId}"]`) : slide.dataset.productMediaId === button.dataset.photoId);
        if (item) slider.append(item);
      });
    }

    queueSync() {
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => this.sync());
    }

    select(id, instant = false) {
      const slide = this.slides.find(item => item.dataset.productMediaId === id);
      if (!slide || slide.offsetWidth === 0) return;
      this.viewer.clearScrollEndTimer?.();
      this.gallery.setActiveMedia(slide.dataset.mediaId);
      if (instant) this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
      this.viewer.update?.();
      this.viewer.disableButtons?.();
      this.sync();
    }

    sync() {
      const active = this.slides.find(slide => slide.classList.contains('is-active') && slide.offsetWidth > 0) || this.slides.find(slide => slide.offsetWidth > 0);
      if (!active) return;
      const id = active.dataset.productMediaId;
      this.buttons.forEach(button => {
        const slide = this.slides.find(item => item.dataset.productMediaId === button.dataset.photoId);
        button.hidden = !slide || slide.offsetWidth === 0;
        if (id === button.dataset.photoId) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
        if (slide) {
          slide.tabIndex = -1;
          const toggle = slide.querySelector('.product__media-toggle');
          if (toggle) {
            toggle.tabIndex = id === button.dataset.photoId ? 0 : -1;
            toggle.setAttribute('aria-label', `Enlarge ${button.dataset.photoDescription}`);
          }
          slide.querySelectorAll('img').forEach(image => image.alt = button.dataset.photoDescription);
        }
      });
      const button = this.buttons.find(item => item.dataset.photoId === id);
      if (button) {
        const box = button.getBoundingClientRect();
        const bounds = this.strip.getBoundingClientRect();
        if (box.left < bounds.left || box.right > bounds.right) this.strip.scrollLeft += box.left - bounds.left - (bounds.width - box.width) / 2;
        if (this.activeId !== id) this.status.textContent = `Photo ${this.buttons.filter(item => !item.hidden).indexOf(button) + 1} of ${this.buttons.filter(item => !item.hidden).length}. ${button.dataset.photoDescription}`;
      }
      this.activeId = id;
    }

    connectModal(signal) {
      this.modal.classList.add('pb40-terra-thumbnails-modal');
      this.modalViewer = this.modal.querySelector('slider-component');
      this.modalSlider = this.modalViewer?.querySelector('[id^="Slider-Gallery"]');
      this.orderSlides(this.modalSlider, true);
      this.buttons.forEach(button => this.modal.querySelectorAll(`img[data-media-id="${button.dataset.photoId}"]`).forEach(image => image.alt = button.dataset.photoDescription));
      this.modalObserver = new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) {
          this.modalWasOpen = true;
          const id = this.modal.openedBy?.dataset.mediaId || this.activeId;
          this.alignModal(id);
          cancelAnimationFrame(this.modalFrame);
          this.modalFrame = requestAnimationFrame(() => this.alignModal(id));
        } else if (this.modalWasOpen) {
          this.modalWasOpen = false;
          const id = this.modalSlider?.querySelector('.is-active [data-media-id]')?.dataset.mediaId;
          if (id) this.select(id, true);
          const toggle = this.slides.find(slide => slide.dataset.productMediaId === this.activeId)?.querySelector('.product__media-toggle');
          toggle?.focus({ preventScroll: true });
        }
      });
      this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      this.modal.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !this.modal.hasAttribute('open')) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 'next' : 'previous';
        const button = this.modalViewer?.querySelector(`button[name="${direction}"]`);
        if (button && !button.disabled) button.click();
      }, { signal });
    }

    alignModal(id) {
      if (!this.modalSlider || !this.modal.hasAttribute('open')) return;
      const target = this.modalSlider.querySelector(`[data-media-id="${id}"]`)?.closest('li');
      if (!target) return;
      this.modalViewer._initHard?.();
      this.modalViewer._markSlidesDirty?.();
      [...this.modalSlider.children].forEach(slide => slide.classList.toggle('is-active', slide === target));
      this.modalSlider.scrollTo({ left: target.offsetLeft, behavior: 'instant' });
      this.modalViewer.resetPages?.();
      this.modalViewer.disableButtons?.();
    }
  });
}
